import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Box,
  Alert,
  CircularProgress,
  Divider,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  PersonAdd as PersonAddIcon,
  Person as PersonIcon,
  Lock as LockIcon,
  Email as EmailIcon,
  AccountCircle as AccountCircleIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  getUserById,
  createUser,
  updateUser 
} from '../../services/api';
import { ROLE_OPTIONS } from '../../constants/roles';

const UserForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!id;
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [formData, setFormData] = useState({
    username: '',
    full_name: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: 'staff'
  });

  useEffect(() => {
    if (isEditMode) {
      fetchUserData();
    } else {
      setLoading(false);
    }
  }, [id]);

  const fetchUserData = async () => {
    try {
      setLoading(true);
      const response = await getUserById(id);
      
      if (response.success) {
        const user = response.data;
        setFormData({
          username: user.username || '',
          full_name: user.full_name || '',
          email: user.email || '',
          password: '',
          confirmPassword: '',
          role: user.role || 'staff'
        });
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch user data');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    // Validate username
    if (!formData.username.trim()) {
      setError('Username is required');
      return false;
    }
    
    // Validate email
    if (!formData.email.trim()) {
      setError('Email is required');
      return false;
    }
    
    if (!/\S+@\S+\.\S+/.test(formData.email)) {
      setError('Please enter a valid email address');
      return false;
    }
    
    // Validate password for new users
    if (!isEditMode && !formData.password) {
      setError('Password is required for new users');
      return false;
    }
    
    if (formData.password && formData.password.length < 6) {
      setError('Password must be at least 6 characters long');
      return false;
    }
    
    // Validate password confirmation
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      
      // Prepare user data according to your database schema
      const userData = {
        username: formData.username,
        full_name: formData.full_name || formData.username, // Use username if full_name not provided
        email: formData.email,
        role: formData.role
      };
      
      // Only include password if provided (for new users or password change)
      if (formData.password) {
        userData.password = formData.password;
      }
      
      let response;
      if (isEditMode) {
        response = await updateUser(id, userData);
      } else {
        response = await createUser(userData);
      }
      
      if (response.success) {
        setSuccess(isEditMode ? 'User updated successfully!' : 'User created successfully!');
        
        setTimeout(() => {
          navigate('/users');
        }, 1500);
      }
    } catch (err) {
      setError(err.message || 'Failed to save user');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/users')}
        sx={{ mb: 3 }}
      >
        Back to Users
      </Button>

      <Paper sx={{ p: 4 }}>
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          {isEditMode ? (
            <PersonIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          ) : (
            <PersonAddIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          )}
          <Typography variant="h4" component="h1">
            {isEditMode ? 'Edit User' : 'Create New User'}
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* User Credentials */}
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                    User Credentials
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Username *"
                        name="username"
                        value={formData.username}
                        onChange={handleChange}
                        required
                        placeholder="Enter username (unique)"
                        InputProps={{
                          startAdornment: <AccountCircleIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        }}
                        helperText="This will be used for login"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Email Address *"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="user@example.com"
                        InputProps={{
                          startAdornment: <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        }}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Personal Information */}
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                    Personal Information (Optional)
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Full Name"
                        name="full_name"
                        value={formData.full_name}
                        onChange={handleChange}
                        placeholder="Enter user's full name"
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Password Section */}
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                    Password
                    {isEditMode && (
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                        (Leave blank to keep current password)
                      </Typography>
                    )}
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Password"
                        name="password"
                        type="password"
                        value={formData.password}
                        onChange={handleChange}
                        required={!isEditMode}
                        placeholder={isEditMode ? "New password (optional)" : "Enter password"}
                        helperText="Minimum 6 characters"
                        InputProps={{
                          startAdornment: <LockIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Confirm Password"
                        name="confirmPassword"
                        type="password"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        required={!isEditMode}
                        placeholder="Confirm password"
                        InputProps={{
                          startAdornment: <LockIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        }}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Role Selection */}
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                    User Role
                  </Typography>
                  <FormControl fullWidth>
                    <InputLabel>Select Role *</InputLabel>
                    <Select
                      label="Select Role *"
                      name="role"
                      value={formData.role}
                      onChange={handleChange}
                      required
                    >
                      {ROLE_OPTIONS.filter((r) => r.value !== 'super_admin').map((r) => (
                        <MenuItem key={r.value} value={r.value}>
                          {r.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    * Administrator has full system access
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Submit Button */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Box display="flex" justifyContent="flex-end" gap={2}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/users')}
                  disabled={saving}
                  size="large"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                  disabled={saving}
                  size="large"
                  sx={{ minWidth: 150 }}
                >
                  {saving ? 'Saving...' : isEditMode ? 'Update User' : 'Create User'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Container>
  );
};

export default UserForm;